import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEOHead from "./SEOHead";
import logo from "../assets/logo.jpg";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ChildSafety.css";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const ChildSafety = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="Child Safety Policy – ComLinkr"
        description="ComLinkr is strictly an 18+ platform with zero tolerance for CSAM or any content that harms or endangers children."
        canonical="https://www.comlinkr.com/child-safety"
      />

      {/* Header */}
      <div className="cs-header">
        <img
          src={logo}
          alt="ComLinkr logo"
          className="cs-header-logo"
          onClick={() => navigate("/")}
        />
        <motion.h1
          className="cs-header-title"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Child Safety Policy
        </motion.h1>
        <motion.p
          className="cs-header-sub"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Protecting children is a non-negotiable priority for ComLinkr.
        </motion.p>
        <span className="cs-badge">Last updated: May 2026</span>
      </div>

      <div className="cs-container">

        {/* 18+ notice */}
        <motion.div
          className="cs-notice"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <span className="cs-notice-icon">🔞</span>
          <p>
            ComLinkr is strictly an <strong>18+ platform</strong>. We do not permit minors to access
            or use our services under any circumstances.
          </p>
        </motion.div>

        {/* Zero Tolerance */}
        <motion.section
          className="cs-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <h2 className="cs-section-title">Zero Tolerance Policy</h2>
          <p className="cs-body">
            ComLinkr has a zero tolerance policy towards child sexual abuse material (CSAM) and any
            content that exploits, harms, or endangers children in any way. Any user found sharing,
            distributing, or engaging with such content will be permanently banned and reported to the
            relevant authorities immediately.
          </p>
        </motion.section>

        <hr className="cs-hr" />

        {/* How We Protect */}
        <motion.section
          className="cs-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          <h2 className="cs-section-title">How We Protect Children</h2>
          <ul className="cs-list">
            {[
              "Age verification is enforced during onboarding",
              "Google Play blocks users determined to be under 18 from downloading our app",
              "All user-generated content is subject to our community guidelines",
              "Any content violating child safety is removed immediately and the account is permanently banned",
            ].map((item) => (
              <li key={item} className="cs-list-item">
                <span className="cs-check">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        <hr className="cs-hr" />

        {/* Reporting */}
        <motion.section
          className="cs-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
        >
          <h2 className="cs-section-title">Reporting</h2>
          <p className="cs-body">
            If you encounter any content or behaviour that endangers a child, report it immediately
            through one of the following channels:
          </p>
          <div className="cs-report-grid">
            <div className="cs-report-card">
              <div className="cs-report-icon">📱</div>
              <h3>In-App Report</h3>
              <p>Use the <strong>Report</strong> button on any post or profile within the ComLinkr app.</p>
            </div>
            <div className="cs-report-card">
              <div className="cs-report-icon">✉️</div>
              <h3>Email Us</h3>
              <p>
                Send details to{" "}
                <a href="mailto:support@comlinkr.com">support@comlinkr.com</a>
                {" "}and our team will respond urgently.
              </p>
            </div>
          </div>
          <p className="cs-authority-note">
            We will report all confirmed violations to relevant national and regional authorities
            without delay.
          </p>
        </motion.section>

        {/* Contact box */}
        <motion.div
          className="cs-contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
        >
          <h2 className="cs-contact-title">See something? Report it immediately.</h2>
          <p className="cs-contact-sub">
            Every report helps keep our community safe. We take all reports seriously.
          </p>
          <a
            href="mailto:support@comlinkr.com?subject=Child Safety Report"
            className="cs-contact-btn"
          >
            Report to support@comlinkr.com
          </a>
          <p className="cs-contact-note">ComLinkr · www.comlinkr.com</p>
        </motion.div>

        <div className="cs-footer">
          <p>© 2026 ComLinkr. All rights reserved.</p>
          <p>
            <a href="/privacy">Privacy Policy</a>
            {" · "}
            <span style={{ cursor: "pointer", color: "#0076EB" }} onClick={() => navigate("/")}>
              Home
            </span>
          </p>
        </div>

      </div>
    </>
  );
};

export default ChildSafety;
